import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  Rating,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Tooltip,
  Avatar,
  IconButton
} from '@mui/material';
import {
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { workOrdersApi } from '../../services/api';

const ContractorPerformanceWidget = ({ 
  companyId = null, 
  maxItems = 10, 
  showHeader = true,
  onViewDetails = null 
}) => {
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPerformance();
  }, [companyId]);

  const fetchPerformance = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {};
      if (companyId) params.companyId = companyId;
      
      const response = await workOrdersApi.getContractorPerformance(params);
      setPerformance(response.data.slice(0, maxItems));
    } catch (error) {
      console.error('Failed to fetch contractor performance:', error);
      setError('Failed to load contractor performance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchPerformance();
  };

  const getPerformanceGrade = (completionRate, avgRating) => {
    const rate = parseFloat(completionRate) || 0;
    const rating = parseFloat(avgRating) || 0;
    
    // Combined score: 70% completion rate, 30% avg rating
    const score = (rate * 0.7) + (rating * 20 * 0.3);
    
    if (score >= 90) return { grade: 'A+', color: 'success' };
    if (score >= 80) return { grade: 'A', color: 'success' };
    if (score >= 70) return { grade: 'B+', color: 'info' };
    if (score >= 60) return { grade: 'B', color: 'info' };
    if (score >= 50) return { grade: 'C+', color: 'warning' };
    if (score >= 40) return { grade: 'C', color: 'warning' };
    return { grade: 'D', color: 'error' };
  };

  const getTrendIcon = (recentWorkOrders, recentCompletions) => {
    const recent = parseInt(recentWorkOrders) || 0;
    const completions = parseInt(recentCompletions) || 0;
    
    if (recent === 0) return null;
    
    const completionRate = (completions / recent) * 100;
    
    if (completionRate >= 80) {
      return <TrendingUpIcon color="success" fontSize="small" />;
    } else if (completionRate >= 50) {
      return <TrendingUpIcon color="info" fontSize="small" />;
    } else {
      return <TrendingDownIcon color="error" fontSize="small" />;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ mr: 2 }} />
            <Typography variant="body2">Loading contractor performance...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" action={
            <Button size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {showHeader && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">
              Contractor Performance
            </Typography>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        )}

        {performance.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No contractor performance data available
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Contractor</TableCell>
                  <TableCell align="center">Grade</TableCell>
                  <TableCell align="center">Work Orders</TableCell>
                  <TableCell align="center">Completion Rate</TableCell>
                  <TableCell align="center">Avg Rating</TableCell>
                  <TableCell align="center">Trend</TableCell>
                  {onViewDetails && <TableCell align="center">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {performance.map((contractor, index) => {
                  const grade = getPerformanceGrade(contractor.completion_rate, contractor.avg_quality_rating);
                  const completionRate = parseFloat(contractor.completion_rate) || 0;
                  
                  return (
                    <TableRow key={contractor.company_id || index} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            <BusinessIcon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {contractor.company_name}
                            </Typography>
                            {contractor.contact_person && (
                              <Typography variant="caption" color="text.secondary">
                                {contractor.contact_person}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Chip
                          label={grade.grade}
                          color={grade.color}
                          size="small"
                          sx={{ fontWeight: 600, minWidth: 40 }}
                        />
                      </TableCell>
                      
                      <TableCell align="center">
                        <Stack alignItems="center" spacing={0.5}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {contractor.total_work_orders}
                          </Typography>
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Active">
                              <Chip
                                label={contractor.active_work_orders}
                                size="small"
                                variant="outlined"
                                color="warning"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            </Tooltip>
                            {contractor.overdue_work_orders > 0 && (
                              <Tooltip title="Overdue">
                                <Chip
                                  label={contractor.overdue_work_orders}
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        </Stack>
                      </TableCell>
                      
                      <TableCell align="center">
                        <Box sx={{ minWidth: 80 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <LinearProgress
                              variant="determinate"
                              value={completionRate}
                              sx={{ flexGrow: 1 }}
                              color={completionRate >= 80 ? 'success' : completionRate >= 60 ? 'info' : 'warning'}
                            />
                            <Typography variant="caption" sx={{ minWidth: 35 }}>
                              {completionRate.toFixed(0)}%
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {contractor.completed_work_orders} / {contractor.total_work_orders}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell align="center">
                        {contractor.avg_quality_rating ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Rating
                              value={parseFloat(contractor.avg_quality_rating)}
                              readOnly
                              size="small"
                              precision={0.1}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {contractor.rated_work_orders} rated
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No ratings
                          </Typography>
                        )}
                      </TableCell>
                      
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          {getTrendIcon(contractor.recent_work_orders, contractor.recent_completions)}
                          <Typography variant="caption" color="text.secondary">
                            {contractor.recent_work_orders} recent
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      {onViewDetails && (
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => onViewDetails(contractor)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {performance.length > 0 && (
          <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Performance Grades:</strong> Based on completion rate (70%) and average rating (30%). 
              Recent trend shows last 30 days activity.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ContractorPerformanceWidget; 